/* eslint-disable react/no-unescaped-entities */
/**
 * OwnerDesk V1 · Phase 2 · Case Detail Page.
 *
 * Route: /proprietari/casi/:caseId
 *
 * Contents:
 *   * Editable property form (features fields: sqm, condition, floor,
 *     bedrooms, bathrooms, rooms, energy_class, heating, ...).
 *   * Latest valuation summary with a "Ricalcola con questi dati"
 *     button that triggers /revaluate; the previous valuation stays
 *     side-by-side so the delta is perceivable.
 *   * PA card (reused component).
 *
 * The property form is optimistic: on PATCH success we keep the local
 * state, so the user can click "Ricalcola" without a full reload.
 */
import { PaSharedResults } from "@/components/owner/PaSharedResults";
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, RotateCw, Trash2, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { enumLabel, featureLabel, featureSelectOptions, statusLabel, semaphoreLabel, stageLabel } from "@/lib/enumLabels";
import { useAuth } from "@/lib/AuthContext";
import Logo from "@/components/Logo";
import PaCard from "@/components/owner/PaCard";
import ConfirmModal from "../components/ConfirmModal";

export default function OwnerCaseDetail() {
  const { caseId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [state, setState] = React.useState({ loading: true, data: null, err: null });
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [revaluating, setRevaluating] = React.useState(false);
  const [previousValuation, setPreviousValuation] = React.useState(null);
  // §LAUNCH-A2 · stato dell'attesa di "Ricalcola" a freddo (timeout gateway).
  const [revaluateWaiting, setRevaluateWaiting] = React.useState(false);
  const [revaluateTimedOut, setRevaluateTimedOut] = React.useState(false);
  const aliveRef = React.useRef(true);
  // Memorizza l'operazione corrente (versione prima del clic + avvio) per
  // riconoscere la NUOVA versione senza mai rilanciare la POST.
  const revalOpRef = React.useRef(null);
  React.useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get(`/v2/owner/cases/${caseId}`);
      setState({ loading: false, data: r.data, err: null });
      const feat = r.data?.property?.features || {};
      setForm({
        surface_sqm: feat.surface_sqm ?? "",
        condition: feat.condition ?? "",
        floor: feat.floor ?? "",
        floors_total: feat.floors_total ?? "",
        rooms: feat.rooms ?? "",
        bathrooms: feat.bathrooms ?? "",
        year_built: feat.year_built ?? "",
        exposure: feat.exposure ?? "",
        brightness: feat.brightness ?? "",
        view: feat.view ?? "",
        occupancy: feat.occupancy ?? "",
        elevator: !!feat.elevator,
        balcony: !!feat.balcony,
        terrace: !!feat.terrace,
        garden: !!feat.garden,
        box: !!feat.box,
        cellar: !!feat.cellar,
        attic: !!feat.attic,
        parking: !!feat.parking,
        // §M8.10 · caratteristiche distintive come lista modificabile.
        // La sorgente è ``feat.free_text_notes`` (stringa) — la
        // splittiamo in bullet items su newline/pipe/virgola per
        // permettere add/remove atomici, poi ricostruiamo la stringa
        // sul submit così l'LLM di feature-uplift (M8.6) riceve lo
        // stesso formato che riceverebbe da VALUTA.
        distinctive_features: _splitFeatureList(feat.free_text_notes),
      });
    } catch (e) {
      setState({ loading: false, data: null, err: e?.response?.data?.detail || "Errore caricamento caso" });
    }
  }, [caseId]);

  React.useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // §M8.10 · list-editable "Caratteristiche distintive".
  const addDistinctive = (text) => {
    const t = (text || "").trim();
    if (!t) return;
    setForm((f) => ({
      ...f,
      distinctive_features: [...(f.distinctive_features || []), t],
    }));
  };
  const removeDistinctive = (idx) => {
    setForm((f) => ({
      ...f,
      distinctive_features: (f.distinctive_features || []).filter(
        (_, i) => i !== idx,
      ),
    }));
  };

  const patch = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form)
          .filter(([k]) => k !== "distinctive_features")
          .filter(([, v]) => v !== "" && v !== undefined)
          .map(([k, v]) => {
            if (["surface_sqm"].includes(k)) return [k, parseFloat(v)];
            if (["floors_total", "rooms", "bathrooms", "year_built"].includes(k))
              return [k, parseInt(v, 10)];
            return [k, v];
          }),
      );
      // §M8.10 · serializza la lista in una stringa multi-linea che
      // ``feature_extraction_service`` legge come farebbe da VALUTA.
      const notes = (form.distinctive_features || [])
        .map((s) => (s || "").trim())
        .filter(Boolean)
        .join("\n");
      payload.free_text_notes = notes;  // "" cancella il vecchio valore
      await api.patch(`/v2/owner/cases/${caseId}/property`, payload);
      toast.success("Dati aggiornati.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const [deleting, setDeleting] = React.useState(false);
  const deleteCase = async () => {
    if (!window.confirm(
      "Confermi di voler eliminare questa analisi?\n\nL'operazione è definitiva e non può essere annullata.",
    )) return;
    setDeleting(true);
    try {
      await api.delete(`/v2/owner/cases/${caseId}`);
      toast.success("Analisi eliminata.");
      nav("/proprietari/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'eliminazione");
      setDeleting(false);
    }
  };

  // §LAUNCH-A2 · /revaluate NON è idempotente (nuovo op_id e nuova versione a
  // ogni POST). Quando il gateway taglia la risposta a freddo (~61s → 502) il
  // backend completa comunque e crea la nuova versione. Qui NON rilanciamo mai
  // la POST: leggiamo (solo GET) finché compare una valutazione con
  // analysis_id DIVERSO da quello precedente e creata dopo l'avvio
  // dell'operazione corrente. Così distinguiamo il risultato dell'operazione
  // corrente da versioni precedenti o da ricalcoli di altre schede/dispositivi.
  const pollForNewVersion = React.useCallback(async () => {
    const op = revalOpRef.current;
    if (!op) return false;
    const deadline = Date.now() + 150000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 5000));
      if (!aliveRef.current) return false;
      try {
        const r = await api.get(`/v2/owner/cases/${caseId}`); // sola lettura
        const lv = r.data?.latest_valuation;
        if (lv?.analysis_id && lv.analysis_id !== op.prevAnalysisId) {
          const created = lv.created_at ? new Date(lv.created_at).getTime() : 0;
          // tolleranza di clock di 10s; se non c'era una versione precedente
          // qualunque nuova valutazione va bene.
          if (!op.prevAnalysisId || created === 0 || created >= op.opStart - 10000) {
            setState({ loading: false, data: r.data, err: null });
            return true;
          }
        }
      } catch (_) { /* continua a leggere */ }
    }
    return false;
  }, [caseId]);

  const revaluate = async () => {
    setRevaluating(true);
    setRevaluateTimedOut(false);
    setRevaluateWaiting(false);
    revalOpRef.current = {
      prevAnalysisId: state.data?.latest_valuation?.analysis_id || null,
      opStart: Date.now(),
    };
    setPreviousValuation(state.data?.latest_valuation || null);
    setUpliftsSummary(null);
    try {
      // First persist any pending edits so the revaluation uses the
      // current form snapshot.
      await patch();
      const resp = await api.post(`/v2/owner/cases/${caseId}/revaluate`);
      setUpliftsSummary(resp.data?.feature_uplifts || []);
      await load();
      if (resp.data?.debounced) {
        toast.success("Analisi già aggiornata un attimo fa: mostro quella.");
      } else {
        toast.success("Nuova stima disponibile.");
      }
    } catch (e) {
      const st = e?.response?.status;
      // Timeout del gateway: il ricalcolo prosegue lato server. NON rilanciare
      // la POST (creerebbe una versione duplicata e un secondo costo AI):
      // attendi in SOLA LETTURA la comparsa della nuova versione.
      const gatewayTimeout = !e?.response || st === 502 || st === 503
        || st === 504 || e?.code === "ECONNABORTED";
      if (gatewayTimeout) {
        setRevaluateWaiting(true);
        const ok = await pollForNewVersion();
        setRevaluateWaiting(false);
        if (ok) {
          toast.success("Nuova stima disponibile.");
        } else {
          setRevaluateTimedOut(true);
        }
      } else {
        toast.error(e?.response?.data?.detail || "Errore durante il ricalcolo");
      }
    } finally {
      setRevaluating(false);
    }
  };

  // "Riprendi": riprende la SOLA LETTURA (nessuna nuova POST di Ricalcola).
  const resumeRevaluate = async () => {
    setRevaluateTimedOut(false);
    setRevaluateWaiting(true);
    const ok = await pollForNewVersion();
    setRevaluateWaiting(false);
    if (ok) toast.success("Nuova stima disponibile.");
    else setRevaluateTimedOut(true);
  };

  // "Aggiorna": rilegge soltanto il caso (sola lettura), senza riconoscere la
  // versione — ripiego minimo richiesto.
  const refreshCase = async () => {
    setRevaluateTimedOut(false);
    await load();
  };
  const [upliftsSummary, setUpliftsSummary] = React.useState(null);

  if (state.loading) return <Shell><Loading /></Shell>;
  if (state.err) return <Shell><ErrorBox msg={state.err} /></Shell>;

  const { data } = state;
  const c = data.case || {};
  const p = data.property || {};
  const latest = data.latest_valuation;
  const _a = p.address;
  const addrLine = (typeof _a === "string" ? _a
    : [_a?.address || _a?.street, _a?.municipality].filter(Boolean).join(" · "))
    || "Immobile senza indirizzo";

  return (
    <Shell>
      <div className="mb-8">
        <button onClick={() => nav("/proprietari/dashboard")}
                className="text-sm text-gray-500 hover:text-[#B40101] inline-flex items-center gap-1.5 mb-4"
                data-testid="owner-detail-back">
          <ArrowLeft size={16} /> Torna alle analisi
        </button>
        <h1 className="text-3xl font-bold text-[#0A0A0A]" data-testid="owner-detail-title">
          {addrLine}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Stato: <strong className="text-[#0A0A0A]">{stageLabel(c.stage) || "in corso"}</strong>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* left column · property form */}
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-2xl bg-white border border-gray-200 p-6"
                   data-testid="owner-detail-property-form">
            <h2 className="text-lg font-bold text-[#0A0A0A] mb-1">Dati dell'immobile</h2>
            <p className="text-sm text-gray-500 mb-5">
              Aggiorna i dati per ottenere una stima più precisa. Le modifiche
              di indirizzo/comune richiedono una nuova analisi.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Num label="Superficie (m²)" v={form.surface_sqm} k="surface_sqm" upd={upd} tid="feat-sqm" />
              <Select label="Condizione" v={form.condition} k="condition" upd={upd} tid="feat-condition" field="condition" />
              <Select label="Piano" v={form.floor} k="floor" upd={upd} tid="feat-floor" field="floor" />
              <Num label="Piani totali edificio" v={form.floors_total} k="floors_total" upd={upd} tid="feat-floors-total" />
              <Num label="Locali" v={form.rooms} k="rooms" upd={upd} tid="feat-rooms" />
              <Num label="Bagni" v={form.bathrooms} k="bathrooms" upd={upd} tid="feat-bathrooms" />
              <Num label="Anno costruzione" v={form.year_built} k="year_built" upd={upd} tid="feat-year-built" />
              <Select label="Esposizione" v={form.exposure} k="exposure" upd={upd} tid="feat-exposure" field="exposure" />
              <Select label="Luminosità" v={form.brightness} k="brightness" upd={upd} tid="feat-brightness" field="brightness" />
              <Select label="Vista" v={form.view} k="view" upd={upd} tid="feat-view" field="view" />
              <Select label="Occupazione" v={form.occupancy} k="occupancy" upd={upd} tid="feat-occupancy" field="occupancy" />
            </div>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Check label="Ascensore" v={form.elevator} k="elevator" upd={upd} tid="feat-elevator" />
              <Check label="Balcone" v={form.balcony} k="balcony" upd={upd} tid="feat-balcony" />
              <Check label="Terrazzo" v={form.terrace} k="terrace" upd={upd} tid="feat-terrace" />
              <Check label="Giardino" v={form.garden} k="garden" upd={upd} tid="feat-garden" />
              <Check label="Box auto" v={form.box} k="box" upd={upd} tid="feat-box" />
              <Check label="Cantina" v={form.cellar} k="cellar" upd={upd} tid="feat-cellar" />
              <Check label="Soffitta / Mansarda" v={form.attic} k="attic" upd={upd} tid="feat-attic" />
              <Check label="Posto auto" v={form.parking} k="parking" upd={upd} tid="feat-parking" />
            </div>

            <DistinctiveFeaturesEditor
              items={form.distinctive_features || []}
              onAdd={addDistinctive}
              onRemove={removeDistinctive}
            />

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={patch} disabled={saving || revaluating || deleting}
                      className="vg-btn-outline inline-flex items-center gap-2"
                      data-testid="owner-detail-save">
                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                Salva
              </button>
              <button onClick={revaluate} disabled={revaluating || saving || deleting}
                      className="vg-btn-primary inline-flex items-center gap-2"
                      data-testid="owner-detail-revaluate">
                {revaluating ? <Loader2 size={18} className="animate-spin" /> : <RotateCw size={18} />}
                Ricalcola con questi dati
              </button>
              <button onClick={deleteCase} disabled={deleting || saving || revaluating}
                      className="ml-auto inline-flex items-center gap-2 text-red-700 border border-red-300 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                      data-testid="owner-detail-delete">
                {deleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
                Elimina analisi
              </button>
            </div>

            {/* §LAUNCH-A2 · attesa/ripresa del ricalcolo a freddo */}
            {revaluateWaiting && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900"
                   data-testid="owner-detail-revaluate-waiting">
                <p className="font-medium">Stiamo ricalcolando…</p>
                <p className="mt-1">
                  Può richiedere circa due minuti; i tuoi dati sono salvati.
                  Attesa massima circa 150 secondi.
                </p>
              </div>
            )}
            {revaluateTimedOut && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900"
                   data-testid="owner-detail-revaluate-timeout">
                <p className="font-medium">Ci sta mettendo più del previsto</p>
                <p className="mt-1 mb-3">
                  Il ricalcolo è ancora in corso e i tuoi dati sono salvati.
                  Puoi riprendere tra poco: non perderai nulla.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button onClick={resumeRevaluate} disabled={revaluating}
                          className="vg-btn-primary inline-flex items-center gap-2"
                          data-testid="owner-detail-revaluate-resume">
                    <RotateCw size={16} /> Riprendi
                  </button>
                  <button onClick={refreshCase} disabled={revaluating}
                          className="vg-btn-outline inline-flex items-center gap-2"
                          data-testid="owner-detail-revaluate-refresh">
                    Aggiorna
                  </button>
                </div>
              </div>
            )}
          </section>

          {latest && (
            <ValuationBlock latest={latest} previous={previousValuation}
                            revaluating={revaluating}
                            upliftsSummary={upliftsSummary} />
          )}

          <AnalysisView data={data} latest={latest} property={p}
                        onRevaluate={revaluate} revaluating={revaluating}
                        onConsentRevoked={() => setState((s) => ({
                          ...s, data: { ...s.data, pa_request_state: "revoked",
                            pa: s.data?.pa?.assignment_source === "v3_request" ? null : s.data?.pa } }))} />
        </div>

        {/* right column · PA card */}
        <aside className="space-y-6">
          <PaCard pa={data.pa} state={data.pa_request_state} />
          <div className="rounded-2xl bg-gray-50 border border-gray-200 p-5 text-sm text-gray-600"
               data-testid="owner-detail-help">
            <p className="font-semibold text-[#0A0A0A] mb-2">Serve aiuto?</p>
            <p>
              {data.pa
                ? "Se qualcosa non torna nella stima o nei tuoi dati, apri il caso con il tuo Property Advisor: potrà guidarti passo passo."
                : "Se qualcosa non torna nella stima o nei tuoi dati, puoi chiedere un confronto gratuito con un Property Advisor dalla sezione AGISCI."}
            </p>
          </div>
        </aside>
      </div>
    </Shell>
  );
}


// ---------------------- valuation block ------------------------------------

function ValuationBlock({ latest, previous, revaluating, upliftsSummary }) {
  const showComparison = previous
    && previous.analysis_id
    && previous.analysis_id !== latest.analysis_id;
  const hasUplifts = Array.isArray(upliftsSummary) && upliftsSummary.length > 0;
  return (
    <section className="rounded-2xl bg-white border border-gray-200 p-6"
             data-testid="owner-detail-valuation">
      <h2 className="text-lg font-bold text-[#0A0A0A] mb-1">Ultima stima</h2>
      <p className="text-sm text-gray-500 mb-5">
        {latest.valuation_mode === "sale" ? "Valore di vendita in €/m²"
          : "Canone di locazione in €/m²·mese"}
        {latest.created_at && ` · calcolata il ${new Date(latest.created_at).toLocaleString("it-IT")}`}
      </p>

      <div className="grid grid-cols-3 gap-6 mb-6">
        <ValMetric label="Basso" v={latest.unit_value_low} tid="val-low" />
        <ValMetric label="Centrale" v={latest.unit_value_central} tid="val-central" big />
        <ValMetric label="Alto" v={latest.unit_value_high} tid="val-high" />
      </div>

      {hasUplifts && (
        <div className="pt-5 border-t border-gray-100"
             data-testid="owner-detail-uplifts-summary">
          <p className="text-xs uppercase tracking-[0.14em] text-[#B40101] mb-3 font-semibold">
            Impatto delle caratteristiche distintive
          </p>
          <ul className="space-y-2 text-sm">
            {upliftsSummary.map((u, i) => (
              <li key={i}
                  className="rounded-lg bg-[#B40101]/[0.04] border border-[#B40101]/20 p-3"
                  data-testid={`owner-detail-uplift-${u.scenario_key || i}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-semibold text-[#0A0A0A]">
                    {u.scenario_title || u.scenario_key}
                  </span>
                  <span className="text-[#B40101] font-semibold text-sm">
                    {u.pct > 0 ? "+" : ""}{u.pct}%
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                  {u.note}
                </p>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-gray-500 mt-2 italic">
            Il dettaglio completo dei ricavi/costi per scenario è
            visibile in VALUTA e DECIDI.
          </p>
        </div>
      )}

      {showComparison && (
        <div className="pt-5 border-t border-gray-100"
             data-testid="owner-detail-valuation-compare">
          <p className="text-xs uppercase tracking-[0.14em] text-gray-400 mb-3">
            Confronto con la stima precedente
          </p>
          <div className="grid grid-cols-3 gap-6">
            <Delta label="Basso" prev={previous.unit_value_low} curr={latest.unit_value_low} />
            <Delta label="Centrale" prev={previous.unit_value_central} curr={latest.unit_value_central} />
            <Delta label="Alto" prev={previous.unit_value_high} curr={latest.unit_value_high} />
          </div>
        </div>
      )}
      {revaluating && (
        <div className="mt-6 text-sm text-gray-500 flex items-center gap-2"
             data-testid="owner-detail-revaluating">
          <Loader2 className="animate-spin" size={14} /> Ricalcolo in corso…
        </div>
      )}
    </section>
  );
}

function ValMetric({ label, v, big, tid }) {
  return (
    <div data-testid={tid}>
      <p className="text-xs uppercase tracking-[0.14em] text-gray-400 mb-1">{label}</p>
      <p className={big ? "text-2xl font-bold text-[#B40101]" : "text-xl font-semibold text-[#0A0A0A]"}>
        {fmtEur(v)}
      </p>
    </div>
  );
}

function Delta({ label, prev, curr }) {
  if (prev == null || curr == null) return (
    <div>
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className="text-sm text-gray-500">—</p>
    </div>
  );
  const diff = curr - prev;
  const pct = prev ? (diff / prev) * 100 : 0;
  const positive = diff >= 0;
  const arrow = positive ? "▲" : "▼";
  const color = positive ? "text-emerald-600" : "text-red-600";
  return (
    <div>
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className="text-sm text-gray-500">Prima: {fmtEur(prev)}</p>
      <p className={`text-sm font-semibold ${color}`}>
        {arrow} {fmtEur(Math.abs(diff))} ({pct >= 0 ? "+" : ""}{pct.toFixed(1)}%)
      </p>
    </div>
  );
}

function fmtEur(v) {
  if (v == null) return "—";
  return "€ " + Math.round(v).toLocaleString("it-IT");
}


// ---------------------- inputs ---------------------------------------------

function Num({ label, v, k, upd, tid, hint }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#0A0A0A] mb-1.5">{label}</span>
      <input type="number" className="vg-input"
             value={v} onChange={(e) => upd(k, e.target.value)}
             data-testid={tid} />
      {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
    </label>
  );
}

function Text({ label, v, k, upd, tid, hint }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#0A0A0A] mb-1.5">{label}</span>
      <input type="text" className="vg-input"
             value={v} onChange={(e) => upd(k, e.target.value)}
             data-testid={tid} />
      {hint && <span className="text-[10px] text-gray-400 block mt-0.5">{hint}</span>}
    </label>
  );
}

// §dashboard-view · menu a tendina con etichette italiane. Il value del
// <select> è SEMPRE il valore interno invariato (round-trip sicuro):
// valore interno → etichetta → stesso valore interno al salvataggio.
function Select({ label, v, k, upd, tid, field }) {
  const cur = v == null ? "" : String(v);
  const opts = featureSelectOptions(field, cur);
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#0A0A0A] mb-1.5">{label}</span>
      <select className="vg-input" value={cur}
              onChange={(e) => upd(k, e.target.value)}
              data-testid={tid}>
        <option value="">— Seleziona —</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function Check({ label, v, k, upd, tid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-[#0A0A0A]">
      <input type="checkbox" checked={!!v}
             onChange={(e) => upd(k, e.target.checked)}
             data-testid={tid} />
      {label}
    </label>
  );
}


// §M8.10 · Lista modificabile delle "caratteristiche distintive".
//
// La sorgente dati è la stringa ``property.features.free_text_notes``
// (§M8.6). Qui la mostriamo come chip-list per permettere all'utente
// di aggiungere/rimuovere elementi con precisione, invece di editare
// una singola textarea. Sul submit ricostruiamo la stringa
// (joined by newline) così il servizio LLM feature-uplift la legge
// nello stesso identico formato in cui l'avrebbe ricevuta da VALUTA.
function DistinctiveFeaturesEditor({ items, onAdd, onRemove }) {
  const [draft, setDraft] = React.useState("");
  const commit = () => {
    const t = draft.trim();
    if (!t) return;
    onAdd(t);
    setDraft("");
  };
  return (
    <div className="mt-6 border-t border-gray-100 pt-5"
         data-testid="owner-detail-distinctive">
      <p className="text-sm font-semibold text-[#0A0A0A] mb-1">
        Caratteristiche distintive
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Dettagli che rendono unico questo immobile (es. vista lago,
        ingresso indipendente, cabina armadio). Il motore li usa per
        modulare la stima quando pertinenti allo scenario.
      </p>
      {items.length > 0 && (
        <ul className="flex flex-wrap gap-2 mb-3"
            data-testid="owner-detail-distinctive-list">
          {items.map((it, i) => (
            <li key={i}
                data-testid={`owner-detail-distinctive-item-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#B40101]/8 border border-[#B40101]/25 text-[#0A0A0A] text-sm pl-3 pr-1.5 py-1">
              <span>{it}</span>
              <button type="button"
                      onClick={() => onRemove(i)}
                      aria-label={`Rimuovi: ${it}`}
                      data-testid={`owner-detail-distinctive-remove-${i}`}
                      className="w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-white/60 text-[#B40101]">
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 120))}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
          }}
          maxLength={120}
          placeholder="es. ingresso indipendente dal cortile"
          className="vg-input flex-1"
          data-testid="owner-detail-distinctive-input"
        />
        <button
          type="button"
          onClick={commit}
          disabled={!draft.trim()}
          className="vg-btn-outline inline-flex items-center gap-1.5 disabled:opacity-40"
          data-testid="owner-detail-distinctive-add"
        >
          <Plus size={16} /> Aggiungi
        </button>
      </div>
      <p className="text-[10px] text-gray-400 mt-1.5">
        Max 120 caratteri per elemento. Premi Invio o Aggiungi per
        confermare. Le modifiche entrano nel prossimo ricalcolo.
      </p>
    </div>
  );
}


// §M8.10 · Splitta la stringa free_text_notes in una lista di item.
// Il proprietario può aver scritto una frase unica in VALUTA
// (es. "vista lago, ingresso indipendente e cabina armadio") oppure
// una lista già separata (newline / pipe / punto e virgola). Qui
// gestiamo tutti i separatori comuni.
function _splitFeatureList(raw) {
  if (!raw || typeof raw !== "string") return [];
  return raw
    .split(/[\n;|]+|,\s+e\s+|,(?!\d)/i)  // no split on "1,5m"
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}


// ---------------------- shell + fallback -----------------------------------

function Shell({ children }) {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/"><Logo size={24} /></Link>
          <span className="text-sm text-gray-600">
            Ciao, <strong className="text-[#0A0A0A]">{user?.nome}</strong>
          </span>
        </div>
      </header>
      <main className="max-w-[1100px] mx-auto px-6 py-10">{children}</main>
    </div>
  );
}

function Loading() {
  return (
    <div className="text-center py-16" data-testid="owner-detail-loading">
      <Loader2 className="animate-spin mx-auto text-[#B40101]" size={28} />
      <p className="text-sm text-gray-500 mt-3">Sto caricando il caso…</p>
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div className="rounded-xl bg-red-50 border border-red-200 p-6"
         data-testid="owner-detail-error">
      <p className="text-red-800 font-semibold">{msg}</p>
    </div>
  );
}


// §dashboard-view · vista dell'ANALISI salvata (solo lettura, nessun
// ricalcolo): Immobile · Valore · Introduzione di zona · Scenari · Prossimi
// passi. Salva/Ricalcola/Elimina restano nel form sopra.
const _eur = (v) =>
  v == null ? "—" : "€ " + Math.round(Number(v)).toLocaleString("it-IT");

function _range(r, suffix = "") {
  if (!r) return null;
  const lo = r.low, hi = r.high;
  if (lo == null && hi == null) return null;
  if (lo === hi || hi == null) return _eur(lo) + suffix;
  return `${_eur(lo)} – ${_eur(hi)}${suffix}`;
}

const _PROPERTY_ORDER = [
  "property_type", "surface_sqm", "condition", "floor", "floors_total",
  "rooms", "bedrooms", "bathrooms", "year_built", "exposure", "brightness",
  "view", "occupancy", "elevator", "balcony", "terrace", "garden", "box",
  "cellar", "attic", "parking",
];

function PropertyView({ property }) {
  const f = (property && property.features) || {};
  const pt = property && property.property_type;
  const rows = [];
  for (const k of _PROPERTY_ORDER) {
    let v = k === "property_type" ? (f[k] ?? pt) : f[k];
    if (v === null || v === undefined || v === "") continue;
    let display;
    if (k === "surface_sqm") display = `${v} m²`;
    else if (k === "year_built" || k === "floors_total" || k === "rooms"
             || k === "bedrooms" || k === "bathrooms") display = String(v);
    else display = enumLabel(k, v);
    if (display == null || display === "") continue;
    rows.push([featureLabel(k), display]);
  }
  if (rows.length === 0) return null;
  return (
    <Section title="Immobile" testid="owner-view-property">
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
        {rows.map(([label, val], i) => (
          <div key={i} data-testid={`owner-view-feat-${i}`}>
            <dt className="text-xs uppercase tracking-[0.1em] text-gray-400">{label}</dt>
            <dd className="text-[#0A0A0A] font-medium">{val}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

function ValueView({ latest }) {
  if (!latest) return null;
  const hasAbs = latest.absolute_value_central != null;
  return (
    <Section title="Valore" testid="owner-view-value">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Metric label="Valore centrale"
                value={hasAbs ? _eur(latest.absolute_value_central)
                              : _eur(latest.unit_value_central)}
                unit={hasAbs ? "" : "€/m²"} />
        <Metric label="Fascia prudente"
                value={hasAbs ? _eur(latest.absolute_value_low)
                              : _eur(latest.unit_value_low)} />
        <Metric label="Fascia alta"
                value={hasAbs ? _eur(latest.absolute_value_high)
                              : _eur(latest.unit_value_high)} />
        <Metric label="Valore al m²" value={_eur(latest.unit_value_central)} unit="€/m²" />
      </div>
    </Section>
  );
}

function ZoneNarrativeView({ latest }) {
  const text = latest && latest.zone_narrative;
  if (!text || typeof text !== "string" || !text.trim()) return null; // nascondi
  const paras = text.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  return (
    <Section title="Introduzione di zona" testid="owner-view-zone-narrative">
      <div className="space-y-3 max-w-[760px]">
        {paras.map((p, i) => (
          <p key={i} className="text-[#0A0A0A] leading-relaxed">{p}</p>
        ))}
      </div>
    </Section>
  );
}

const _SEM_COLORS = {
  GREEN: "bg-green-100 text-green-800",
  AMBER: "bg-amber-100 text-amber-800",
  YELLOW: "bg-amber-100 text-amber-800",
  RED: "bg-red-100 text-red-800",
};

function ScenarioCard({ s }) {
  // §Micro8 · segnaposto "da_analizzare": solo visualizzazione, nessun
  // voto/semaforo/numeri, non confrontabile.
  if (s.status === "da_analizzare") {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 p-4 bg-gray-50/60"
           data-testid={`owner-scenario-${s.scenario_key}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-bold text-[#0A0A0A]">{s.title}</p>
            {s.strategy_description && (
              <p className="text-sm text-gray-500 mt-0.5">{s.strategy_description}</p>
            )}
          </div>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 flex-shrink-0"
                data-testid={`owner-scenario-placeholder-${s.scenario_key}`}>
            Da analizzare
          </span>
        </div>
      </div>
    );
  }
  const ea = s.economic_analysis || {};
  const sem = s.semaphore || {};
  const net = _range(ea.net_proceeds_range) || _range(ea.annual_net_rent_range);
  const netLabel = ea.annual_net_rent_range ? "Rendita netta annua" : "Ricavo netto";
  const costs = _range(ea.upfront_costs_range) || _range(ea.transaction_costs_range)
    || _range(ea.preparation_costs_range);
  const texec = ea.time_to_execute_months;
  const timeStr = texec
    ? (texec.low === texec.high ? `${texec.low} mesi` : `${texec.low}–${texec.high} mesi`)
    : null;
  return (
    <div className="rounded-xl border border-gray-200 p-4" data-testid={`owner-scenario-${s.scenario_key}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-[#0A0A0A]">
            {s.title}{s.is_primary && <span className="ml-2 text-xs text-[#B40101]">principale</span>}
          </p>
          {s.strategy_description && (
            <p className="text-sm text-gray-500 mt-0.5">{s.strategy_description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-sm font-bold text-[#0A0A0A]">{s.score}/100</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${_SEM_COLORS[sem.state] || "bg-gray-100 text-gray-700"}`}>
            {sem.label || sem.state}
          </span>
        </div>
      </div>
      {s.score_rationale && <p className="text-sm text-gray-600 mt-2">{s.score_rationale}</p>}
      <div className="flex flex-wrap gap-x-8 gap-y-2 mt-3 text-sm">
        {net && <span><span className="text-gray-400">{netLabel}:</span> <strong>{net}</strong></span>}
        {costs && <span><span className="text-gray-400">Costi:</span> <strong>{costs}</strong></span>}
        {timeStr && <span><span className="text-gray-400">Tempi:</span> <strong>{timeStr}</strong></span>}
      </div>
    </div>
  );
}

function ScenariosView({ decision }) {
  const scenarios = (decision && decision.scenarios || [])
    .filter((s) => !s.exclude_from_cards)
    // §Micro8 · student_rent resta nascosto finché il backend non lo
    // supporta (coerente con VALUTA).
    .filter((s) => !(s.status === "da_analizzare"
                     && String(s.scenario_key || "").toUpperCase() === "STUDENT_RENT"));
  if (scenarios.length === 0) return null;
  return (
    <Section title="Scenario principale e alternative" testid="owner-view-scenarios">
      <div className="space-y-3">
        {scenarios.map((s) => <ScenarioCard key={s.scenario_key} s={s} />)}
      </div>
    </Section>
  );
}

function NextStepsView({ nextSteps }) {
  if (!nextSteps) return null;
  const docs = nextSteps.documents || [];
  const roles = nextSteps.professional_roles || [];
  if (docs.length === 0 && roles.length === 0) return null;
  const label = (x) => (typeof x === "string" ? x
    : (x && (x.label || x.name || x.title || x.role || x.document_type)) || null);
  return (
    <Section title="Prossimi passi" testid="owner-view-next-steps">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {docs.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Documenti suggeriti</p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-[#0A0A0A]">
              {docs.map((d, i) => { const l = label(d); return l ? <li key={i}>{l}</li> : null; })}
            </ul>
          </div>
        )}
        {roles.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.1em] text-gray-400 mb-2">Professionisti suggeriti</p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-[#0A0A0A]">
              {roles.map((r, i) => { const l = label(r); return l ? <li key={i}>{l}</li> : null; })}
            </ul>
          </div>
        )}
      </div>
    </Section>
  );
}

// §BLOCCO1-P1 · Navigazione della decisione dalla Scrivania. Il backend
// (`decision_status`) dice se la scelta è corrente, superata o assente, e se
// esiste un piano corrente — il frontend NON ricalcola la validità A-8.
export function DecisionNav({ status, caseId }) {
  const nav = useNavigate();
  if (!status || !status.has_decision || !caseId) return null;
  const sup = status.superseded_previous_choice;
  const cur = status.current_choice;
  const decidiUrl = `/vg2/decidi/${caseId}`;
  const agisciUrl = `/vg2/agisci/${caseId}`;
  const fmtDate = (v) => {
    if (!v) return null;
    try { return new Date(v).toLocaleDateString("it-IT"); } catch (e) { return null; }
  };
  const supTitle = sup && (sup.selected_scenario_title || sup.selected_scenario_key);
  const curTitle = cur && (cur.selected_scenario_title || cur.selected_scenario_key);
  return (
    <Section title="La tua decisione" testid="owner-decision-nav">
      {sup && !cur && (
        <div className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-4"
             data-testid="owner-decision-superseded">
          <p className="text-sm font-semibold text-amber-900 mb-1">
            L'analisi è stata aggiornata
          </p>
          <p className="text-sm text-amber-800">
            La tua scelta precedente
            {supTitle ? <> (<strong>{supTitle}</strong>)</> : null}
            {fmtDate(sup.created_at) ? <> del {fmtDate(sup.created_at)}</> : null}
            {" "}è conservata nello storico, ma non è più attiva su questa
            nuova analisi. Conferma una nuova scelta per proseguire.
          </p>
          <button type="button" onClick={() => nav(decidiUrl)}
                  className="vg-btn-primary inline-flex items-center gap-2 mt-3"
                  data-testid="owner-decision-reconfirm-btn">
            Conferma una nuova scelta
          </button>
        </div>
      )}
      {cur && (
        <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4"
             data-testid="owner-decision-current">
          <p className="text-xs uppercase tracking-[0.1em] text-gray-400 mb-1">
            Scelta corrente
          </p>
          <p className="text-[#0A0A0A] font-medium flex items-center flex-wrap gap-2">
            <span>{curTitle || "Decisione registrata"}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800"
                  data-testid="owner-decision-current-badge">corrente</span>
          </p>
          {status.has_current_plan && (
            <button type="button" onClick={() => nav(agisciUrl)}
                    className="vg-btn-outline inline-flex items-center gap-2 mt-3"
                    data-testid="owner-decision-goto-agisci">
              Vai al piano operativo (AGISCI)
            </button>
          )}
        </div>
      )}
      <button type="button" onClick={() => nav(decidiUrl)}
              className="text-sm text-[#B40101] font-medium"
              data-testid="owner-decision-goto-decidi">
        Vedi le alternative in DECIDI
      </button>
    </Section>
  );
}

// §BLOCCO2 · Controllo del consenso alla condivisione con un Property
// Advisor. Il proprietario può revocarlo: alla revoca il PA perde subito
// l'accesso (il backend gestisce consenso + accesso; lo storico resta).
export function ConsentControl({ caseId, onRevoked }) {
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const refresh = React.useCallback(() => {
    if (!caseId) return;
    api.get(`/v2/property-advisor/requests/case/${caseId}`)
      .then((r) => setState(r.data))
      .catch(() => setState({ exists: false }));
  }, [caseId]);
  React.useEffect(() => { refresh(); }, [refresh]);
  if (!state || !state.exists) return null;
  const revoked = state.consent_status === "REVOKED";
  async function revoke() {
    setConfirming(false);
    setBusy(true);
    try {
      await api.post(`/v2/property-advisor/requests/case/${caseId}/revoke-consent`, {});
      toast.success("Consenso revocato: il Property Advisor non ha più accesso.");
      refresh();
      onRevoked?.();
    } catch (e) {
      toast.error("Revoca non riuscita.");
    } finally { setBusy(false); }
  }
  return (
    <Section title="Condivisione con il Property Advisor" testid="owner-consent-control">
      <p className="text-sm text-[#4B5563] mb-3">
        Hai richiesto un confronto con un Property Advisor. Stato del consenso alla
        condivisione dei tuoi dati:{" "}
        <strong data-testid="owner-consent-status">{revoked ? "revocato" : "attivo"}</strong>.
      </p>
      {!revoked && (
        <button type="button" onClick={() => setConfirming(true)} disabled={busy}
                className="vg-btn-outline text-sm disabled:opacity-50"
                data-testid="owner-consent-revoke-btn">
          Revoca il consenso alla condivisione
        </button>
      )}
      <ConfirmModal
        open={confirming}
        title="Revocare il consenso?"
        message="Il Property Advisor perderà subito l'accesso al caso e ai tuoi recapiti. Potrai presentare una nuova richiesta in seguito, con un nuovo consenso."
        confirmLabel="Revoca il consenso"
        destructive
        onConfirm={revoke}
        onCancel={() => setConfirming(false)}
        testid="owner-consent-revoke-confirm"
      />
      {revoked && (
        <p className="text-sm text-amber-800" data-testid="owner-consent-revoked-note">
          Il consenso è stato revocato. Il professionista non può più vedere il
          caso né i tuoi recapiti. Le registrazioni storiche restano conservate.{" "}
          <a href={`/vg2/agisci/${caseId}`} className="underline font-medium"
             data-testid="owner-consent-new-request-link">Presenta una nuova richiesta</a>
        </p>
      )}
    </Section>
  );
}

function AnalysisView({ data, latest, property, onRevaluate, revaluating, onConsentRevoked }) {
  const hasAnalysis = !!latest;
  if (!hasAnalysis) {
    return (
      <section className="rounded-2xl bg-white border border-gray-200 p-6"
               data-testid="owner-view-no-analysis">
        <p className="text-[#0A0A0A] font-medium mb-3">Analisi non ancora eseguita</p>
        <button onClick={onRevaluate} disabled={revaluating}
                className="vg-btn-primary inline-flex items-center gap-2"
                data-testid="owner-view-run-analysis">
          {revaluating ? <Loader2 size={18} className="animate-spin" /> : <RotateCw size={18} />}
          Esegui l'analisi
        </button>
      </section>
    );
  }
  return (
    <>
      <PropertyView property={property} />
      <ValueView latest={latest} />
      <ZoneNarrativeView latest={latest} />
      <ScenariosView decision={data.decision_view} />
      <DecisionNav status={data.decision_status} caseId={data.case?.case_id} />
      <ConsentControl caseId={data.case?.case_id} onRevoked={onConsentRevoked} />
      <PaSharedResults caseId={data.case?.case_id} />
      <NextStepsView nextSteps={data.next_steps} />
      <VersionsHistory caseId={data.case?.case_id}
                       currentAnalysisId={latest?.analysis_id} />
    </>
  );
}

function Section({ title, testid, children }) {
  return (
    <section className="rounded-2xl bg-white border border-gray-200 p-6" data-testid={testid}>
      <h2 className="text-lg font-bold text-[#0A0A0A] mb-4">{title}</h2>
      {children}
    </section>
  );
}

function Metric({ label, value, unit }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.1em] text-gray-400 mb-1">{label}</p>
      <p className="text-lg font-bold text-[#0A0A0A]">
        {value}{unit ? <span className="text-xs text-gray-500 font-normal ml-1">{unit}</span> : null}
      </p>
    </div>
  );
}

// §I-2 · Storico versioni (sola lettura). Mostra le valutazioni e le
// versioni DECIDI già salvate. La versione corrente è quella in cima
// (più recente) ed è evidenziata; le precedenti portano la dicitura neutra
// "Analisi storica". Nessun confronto tra input, nessuna etichetta
// "aggiornata/obsoleta". Le versioni DECIDI senza source_analysis_id
// mostrano "collegamento alla valutazione non registrato".
function _fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString("it-IT",
      { day: "2-digit", month: "long", year: "numeric" });
  } catch (e) { return String(v); }
}

function VersionsHistory({ caseId, currentAnalysisId }) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (!caseId) return;
    let alive = true;
    api.get(`/v2/owner/cases/${caseId}/versions`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || "Errore storico"); });
    return () => { alive = false; };
  }, [caseId]);

  const vals = (data && data.valuations) || [];
  const decs = (data && data.decisions) || [];
  if (err) return (
    <Section title="Storico versioni" testid="owner-versions">
      <p className="text-sm text-gray-500" data-testid="owner-versions-error">{err}</p>
    </Section>
  );
  if (!data) return null;
  if (vals.length === 0 && decs.length === 0) return null;

  return (
    <Section title="Storico versioni" testid="owner-versions">
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="text-sm text-[#B40101] font-medium mb-4"
              data-testid="owner-versions-toggle">
        {open ? "Nascondi storico" : `Mostra storico (${vals.length} valutazioni, ${decs.length} versioni DECIDI)`}
      </button>
      {!open ? null : (
        <div className="space-y-6" data-testid="owner-versions-body">
          <div>
            <h3 className="text-xs uppercase tracking-[0.1em] text-gray-400 mb-3">Valutazioni</h3>
            <ul className="space-y-3">
              {vals.map((v, i) => {
                const isCurrent = currentAnalysisId && v.analysis_id === currentAnalysisId;
                const perMonth = v.unit === "eur_per_sqm_per_month";
                return (
                  <li key={v.analysis_id || i}
                      className={`rounded-xl border p-4 ${isCurrent ? "border-[#B40101] bg-red-50/40" : "border-gray-200 bg-white"}`}
                      data-testid={`owner-version-val-${v.version}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold text-[#0A0A0A]">
                        Versione {v.version}
                      </span>
                      {isCurrent
                        ? <span className="text-xs font-bold text-[#B40101]"
                                data-testid="owner-version-current-badge">VERSIONE CORRENTE</span>
                        : <span className="text-xs text-gray-500">
                            Analisi storica, versione del {_fmtDate(v.created_at)}
                          </span>}
                    </div>
                    <div className="text-xs text-gray-500 mb-2">{_fmtDate(v.created_at)}</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-gray-400">{perMonth ? "Canone centrale" : "Valore centrale"}</p>
                        <p className="font-medium text-[#0A0A0A]">
                          {v.unit_value_central == null ? "—"
                            : (perMonth
                                ? `€ ${Math.round(v.unit_value_central)}/m²·mese`
                                : _eur(v.absolute_value_central ?? v.unit_value_central))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Fascia</p>
                        <p className="font-medium text-[#0A0A0A]">
                          {v.unit_value_low == null && v.unit_value_high == null ? "—"
                            : (perMonth
                                ? `€ ${Math.round(v.unit_value_low)} – ${Math.round(v.unit_value_high)}/m²·mese`
                                : _range({ low: v.absolute_value_low ?? v.unit_value_low,
                                           high: v.absolute_value_high ?? v.unit_value_high }))}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Fonte principale</p>
                        <p className="font-medium text-[#0A0A0A]">
                          {v.primary_source && v.primary_source.source_title
                            ? v.primary_source.source_title
                            : (v.evidence_sources_used && v.evidence_sources_used.length
                                ? v.evidence_sources_used.join(", ") : "—")}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.1em] text-gray-400 mb-3">Versioni DECIDI</h3>
            <ul className="space-y-3">
              {decs.map((d, i) => (
                <li key={d.decision_version || i}
                    className={`rounded-xl border p-4 ${i === 0 ? "border-[#B40101] bg-red-50/40" : "border-gray-200 bg-white"}`}
                    data-testid={`owner-version-dec-${d.decision_version}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-semibold text-[#0A0A0A]">
                      Versione DECIDI {d.decision_version}
                    </span>
                    {i === 0
                      ? <span className="text-xs font-bold text-[#B40101]">VERSIONE CORRENTE</span>
                      : <span className="text-xs text-gray-500">
                          Analisi storica, versione del {_fmtDate(d.created_at)}
                        </span>}
                  </div>
                  <div className="text-xs text-gray-500 mb-2">{_fmtDate(d.created_at)}</div>
                  {!d.collegamento_registrato ? (
                    <p className="text-xs text-gray-400 mb-2"
                       data-testid={`owner-version-dec-nolink-${d.decision_version}`}>
                      Collegamento alla valutazione non registrato
                    </p>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    {(d.scenarios || []).map((s, j) => {
                      const st = statusLabel(s.status);
                      const sem = s.status ? null : semaphoreLabel(s.semaphore_label);
                      const tail = st || sem;
                      return (
                        <span key={j}
                              className="inline-flex items-center gap-1 text-xs rounded-full border border-gray-200 px-2 py-0.5 text-gray-600"
                              data-testid={`owner-version-dec-${d.decision_version}-scn-${j}`}>
                          {s.title || enumLabel("scenario_key", s.scenario_key) || "Scenario"}
                          {tail ? ` · ${tail}` : ""}
                        </span>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Section>
  );
}

